<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';

// Cobrança Pix no envio do formulário. O dinheiro sai pela conta Asaas da
// própria empresa (Configurações → Asaas), não pela nossa: aqui só decidimos
// quanto cobrar e por quê.
const { t } = useI18n();

const payment = defineModel({ type: Object, default: () => ({}) });

const props = defineProps({
  fields: { type: Array, default: () => [] },
  funnelId: { type: [Number, String], default: null },
});

const OPTION_TYPES = ['select', 'radio', 'checkbox'];

const enabled = computed(() => !!payment.value?.enabled);
const mode = computed(() => payment.value?.mode || 'fixed');
const camposComOpcao = computed(() =>
  props.fields.filter(
    field => OPTION_TYPES.includes(field.type) && field.key && field.label
  )
);
const campoEscolhido = computed(() =>
  camposComOpcao.value.find(field => field.key === payment.value?.value_field)
);
// Sem funil o lead não vira card, e a cobrança não teria onde morar — o
// backend recusa, então o aviso aparece aqui antes de o cliente publicar.
const semFunil = computed(() => !props.funnelId);

const patch = novo => {
  payment.value = { ...(payment.value || {}), ...novo };
};

const precoDaOpcao = opcao => (payment.value?.option_prices || {})[opcao] || '';

const setPrecoDaOpcao = (opcao, valor) => {
  patch({
    option_prices: { ...(payment.value?.option_prices || {}), [opcao]: valor },
  });
};
</script>

<template>
  <div class="flex flex-col gap-5">
    <label class="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        class="w-4 h-4"
        :checked="enabled"
        @change="patch({ enabled: $event.target.checked })"
      />
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('FORMS_MGMT.EDITOR.PAYMENT.ENABLE') }}
      </span>
    </label>
    <p class="-mt-4 text-xs text-n-slate-11">
      {{ t('FORMS_MGMT.EDITOR.PAYMENT.HINT') }}
    </p>

    <template v-if="enabled">
      <p
        v-if="semFunil"
        class="text-xs text-n-ruby-11 bg-n-ruby-2 rounded-lg px-3 py-2"
      >
        {{ t('FORMS_MGMT.EDITOR.PAYMENT.NEEDS_FUNNEL') }}
      </p>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.PAYMENT.MODE') }}
        </span>
        <select
          :value="mode"
          class="form-select"
          @change="patch({ mode: $event.target.value })"
        >
          <option value="fixed">
            {{ t('FORMS_MGMT.EDITOR.PAYMENT.MODE_FIXED') }}
          </option>
          <option value="field">
            {{ t('FORMS_MGMT.EDITOR.PAYMENT.MODE_FIELD') }}
          </option>
        </select>
      </div>

      <Input
        v-if="mode === 'fixed'"
        :model-value="payment.value"
        :label="t('FORMS_MGMT.EDITOR.PAYMENT.VALUE')"
        placeholder="150,00"
        @update:model-value="valor => patch({ value: valor })"
      />

      <template v-else>
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.PAYMENT.VALUE_FIELD') }}
          </span>
          <select
            :value="payment.value_field || ''"
            class="form-select"
            @change="patch({ value_field: $event.target.value })"
          >
            <option value="">
              {{ t('FORMS_MGMT.EDITOR.PAYMENT.VALUE_FIELD_EMPTY') }}
            </option>
            <option
              v-for="field in camposComOpcao"
              :key="field.key"
              :value="field.key"
            >
              {{ field.label }}
            </option>
          </select>
        </div>

        <div v-if="campoEscolhido" class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.PAYMENT.OPTION_PRICES') }}
          </span>
          <div
            v-for="opcao in campoEscolhido.options || []"
            :key="opcao"
            class="flex items-center gap-3"
          >
            <span class="flex-1 text-sm text-n-slate-11">{{ opcao }}</span>
            <input
              :value="precoDaOpcao(opcao)"
              class="form-input w-32"
              placeholder="0,00"
              @input="setPrecoDaOpcao(opcao, $event.target.value)"
            />
          </div>
        </div>
      </template>

      <Input
        :model-value="payment.description"
        :label="t('FORMS_MGMT.EDITOR.PAYMENT.DESCRIPTION')"
        :placeholder="t('FORMS_MGMT.EDITOR.PAYMENT.DESCRIPTION_PLACEHOLDER')"
        @update:model-value="texto => patch({ description: texto })"
      />
    </template>
  </div>
</template>
